import { useContext, useEffect } from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { alpha, Chip, CircularProgress } from "@mui/material";
import { useMutation, useQuery } from "react-query";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import AuthContext from "../../context/AuthProvider";
import logo from "../../utils/images/ridePlus.png";
import { PROFILE_IMAGE_BASE_URL, PROFILE_IMAGE_URL } from "../../utils/config";
import { CheckCircleOutline, LockOutlined } from "@mui/icons-material";
import Error from "../error";
import useTenantBranding from "../../hooks/useTenantBranding";
import AuthLayout from "../../components/auth/AuthLayout";
import PrimaryButton from "../../components/auth/PrimaryButton";
import SecondaryButton from "../../components/auth/SecondaryButton";

function Consent({ consentId }) {
  const axiosPrivate = useAxiosPrivate();
  const { setCurrentTenant } = useContext(AuthContext);
  const { tenantLogo, hideDefaultBranding } = useTenantBranding();

  // Mutation & Query
  const { isLoading, data, error } = useQuery(
    ["consent"],
    () => {
      return axiosPrivate.get(`/oauth/consent/${consentId}`);
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
    }
  );
  const consentTenantLogo = data?.data?.data?.tenant_logo || tenantLogo;

  const {
    mutate,
    isLoading: approvalLoading,
    data: approvalData,
  } = useMutation(() => {
    return axiosPrivate.post(
      "/oauth/approveConsent",
      {
        consent_id: consentId,
      },
      {
        withCredentials: true,
      }
    );
  });

  const { isFetching: profileIsFetching, data: profile } = useQuery(
    ["user_profile"],
    () => {
      return axiosPrivate.get(`/profile`);
    },
    {
      refetchOnWindowFocus: false,
      enable: false,
    }
  );

  const {
    mutate: rejectMutate,
    isLoading: rejectLoading,
    data: rejectData,
  } = useMutation(() => {
    return axiosPrivate.post(
      "/oauth/rejectConsent",
      {
        consent_id: consentId,
        failure_reason: "User rejected to grant the scopes you requested!",
      },
      {
        withCredentials: true,
      }
    );
  });

  // USEEFFECT
  // Extract tenant information from consent data when available
  useEffect(() => {
    if (data?.data?.data?.tenant_id) {
      setCurrentTenant({
        id: data.data.data.tenant_id,
        name: data.data.data.tenant_name || null, // If tenant name is available
        logo: data.data.data.tenant_logo || null, // If tenant logo is available
      });
    }
  }, [data?.data?.data, setCurrentTenant]);

  useEffect(() => {
    if (data?.data?.data?.client_trusted) {
      mutate();
    }
  }, [data?.data?.data?.client_trusted, mutate]);
  useEffect(() => {
    if (data?.data?.data?.approved) {
      mutate();
    }
  }, [data?.data?.data?.approved, mutate]);

  useEffect(() => {
    if (approvalData?.data?.data?.location) {
      window.location.href = approvalData?.data?.data?.location;
    }
  }, [approvalData?.data?.data?.location]);

  useEffect(() => {
    if (rejectData?.data?.data?.location) {
      window.location.href = rejectData?.data?.data?.location;
    }
  }, [rejectData?.data?.data?.location]);

  // HELPERS
  const onApprove = () => {
    mutate();
  };
  const onReject = () => {
    rejectMutate();
  };

  // RENDER
  if (error) return <Error error={error} />;

  if (
    isLoading ||
    data?.data?.data?.approved ||
    (approvalLoading && data?.data?.data?.client_trusted)
  ) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress size={40} sx={{ color: "secondary.main" }} />
      </Box>
    );
  }

  if (!data?.data?.data || data?.data?.data?.client_trusted) return null;

  return (
    <AuthLayout
      maxWidth="sm"
      showThemeToggle={false}
      header={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {(consentTenantLogo || !hideDefaultBranding) && (
              <Avatar
                src={consentTenantLogo || logo}
                sx={{ width: 28, height: 28, mr: 1.5, borderRadius: 1 }}
              />
            )}
            <Typography
              sx={{ fontSize: 16, fontWeight: 600, color: "text.primary" }}
            >
              {hideDefaultBranding ? "Sign In" : "Sign In with SSO"}
            </Typography>
          </Box>
          <LockOutlined sx={{ fontSize: 20, color: "text.secondary" }} />
        </Box>
      }
      footer={
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column-reverse", sm: "row" },
            gap: 1.5,
          }}
        >
          <SecondaryButton
            onClick={onReject}
            disabled={rejectLoading || approvalLoading}
          >
            {rejectLoading ? <CircularProgress size={20} /> : "Cancel"}
          </SecondaryButton>
          <PrimaryButton
            onClick={onApprove}
            loading={approvalLoading}
            disabled={profileIsFetching || rejectLoading}
            sx={{
              bgcolor: "primary.main",
              color: "primary.contrastText",
              boxShadow: (t) =>
                `0 3px 5px 2px ${alpha(t.palette.primary.main, 0.3)}`,
              "&:hover": {
                bgcolor: "primary.dark",
                boxShadow: (t) =>
                  `0 4px 8px 2px ${alpha(t.palette.primary.main, 0.4)}`,
              },
            }}
          >
            Allow Access
          </PrimaryButton>
        </Box>
      }
    >
      {profileIsFetching ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "40vh",
          }}
        >
          <CircularProgress size={40} sx={{ color: "secondary.main" }} />
        </Box>
      ) : (
        <>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <Avatar
              alt={data?.data?.data?.client_name ?? ""}
              sx={{
                width: "72px",
                height: "72px",
                borderRadius: 2,
                border: "2px solid",
                borderColor: "divider",
                boxShadow: (t) =>
                  `0 4px 12px ${alpha(t.palette.common.black, 0.08)}`,
                bgcolor: "action.hover",
                "& .MuiAvatar-img": { objectFit: "contain", p: 0.5 },
              }}
              src={data?.data?.data?.client_logo}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 600,
                textAlign: "center",
                mb: 0.5,
                color: "text.primary",
              }}
            >
              {data?.data?.data?.client_name}
            </Typography>
            <Typography
              sx={{
                fontSize: 14,
                textAlign: "center",
                color: "text.secondary",
                mb: 2,
              }}
            >
              wants to access your SSO Account
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Chip
                variant="outlined"
                avatar={
                  <Avatar
                    alt="profile_picture"
                    sx={{ width: 28, height: 28 }}
                    src={`${PROFILE_IMAGE_BASE_URL}/${PROFILE_IMAGE_URL}/${profile?.data?.data?.profile_picture}`}
                  />
                }
                label={`${profile?.data?.data?.first_name}${
                  " " + profile?.data?.data?.middle_name
                }`}
                sx={{
                  px: 2,
                  py: 2.5,
                  fontWeight: 500,
                  fontSize: 14,
                  borderColor: "divider",
                  bgcolor: "action.hover",
                  color: "text.primary",
                }}
              />
            </Box>
            <Box
              sx={{
                width: "100%",
                bgcolor: "action.hover",
                p: 2.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "text.secondary",
                  mb: 1.5,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                This app will be able to:
              </Typography>
              {data?.data?.data?.scopes?.map((item) => (
                <Box
                  key={item.name}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    mb: 1.5,
                    "&:last-child": { mb: 0 },
                  }}
                >
                  <CheckCircleOutline
                    sx={{
                      fontSize: 18,
                      color: "success.main",
                      mr: 1.5,
                      mt: 0.2,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{ fontSize: 14, color: "text.primary", lineHeight: 1.5 }}
                  >
                    {item.description}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </>
      )}
    </AuthLayout>
  );
}
export default Consent;
